import { useState } from 'react';
import { useFormikContext } from 'formik';
import { Button, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/AddOutlined';
import { AppointmentType } from '@prisma/client';
import {
  CreateAptValues,
  DEFAULT_APT_LENGTH,
} from '../../utils/form/appointmentForm';
import {
  getLengthOptions,
  getTakenSessionSlots,
  updateSessionStart,
} from '../../helpers/appointments';
import useUser from '../../hooks/global/useUser';
import useCalendarSelectedAppointment from '../../hooks/appointments/useCalendarSelectedAppointment';
import theme from '../../constants/theme';
import FormSmallModal, { FormData } from '../global/FormSmallModal';
import {
  durationFormSchema,
  DurationFormState,
} from '../../types/appointments';
import CalendarInput, { getCalendarInputName } from './CalendarInput';

/* The design's grey chip, shared by both actions under the calendar. */
const ACTION_SX = {
  minWidth: 0,
  py: 1.25,
  px: 2,
  borderRadius: '5px',
  fontSize: 'inherit',
  fontWeight: 'fontWeightMedium',
  border: 1,
  borderColor: 'var(--lineGrey)',
  color: 'text.primary',
  background: theme.palette.lightGrey.main,
  '&:hover': {
    background: theme.palette.lightGrey.main,
    borderColor: theme.palette.common.black,
  },
};

interface AppointmentDateProps {
  allowSwitchToBookingLink?: boolean;
}

function AppointmentDate({
  allowSwitchToBookingLink = false,
}: AppointmentDateProps) {
  const { selectedAppointment } = useCalendarSelectedAppointment();
  const { values, touched, setFieldValue, setFieldTouched, errors } =
    useFormikContext<CreateAptValues>();
  const { user } = useUser();

  const [isDurationModalOpen, setIsDurationModalOpen] = useState(false);

  const allowMultiSession =
    values.appointmentType === 'NEW_APPOINTMENT' && !selectedAppointment;

  const onChangeMainSession = (value: Date) => {
    setFieldValue('startDateTime', value);
  };

  const onChangeSession = (index: number, value: Date) => {
    const next = updateSessionStart(
      values.additionalAppointments || [],
      index,
      value,
    );
    if (next) setFieldValue('additionalAppointments', next);
  };

  const onAddSession = (appointmentLengthInMinutes: string) => {
    const additionalAppointments = values.additionalAppointments || [];
    additionalAppointments.push({
      appointmentLengthInMinutes:
        appointmentLengthInMinutes || DEFAULT_APT_LENGTH,
      startDateTime: '',
      endDateTime: '',
    });
    setFieldValue('additionalAppointments', additionalAppointments);
  };

  const onDeleteSession = (index: number) => {
    if (index < 0) {
      // move all appointments up and remove the first one, setting the first one to the original appointment
      const additionalAppointmentsClone = [
        ...(values.additionalAppointments || []),
      ];
      const mainSession = additionalAppointmentsClone.shift();
      if (!mainSession) return;

      setFieldValue('startDateTime', mainSession.startDateTime);
      setFieldValue('endDateTime', mainSession.endDateTime);
      setFieldValue(
        'appointmentLengthInMinutes',
        mainSession.appointmentLengthInMinutes,
      );
      setFieldValue('additionalAppointments', additionalAppointmentsClone);
    } else {
      const additionalAppointments = values.additionalAppointments || [];
      additionalAppointments.splice(index, 1);
      setFieldValue('additionalAppointments', additionalAppointments);
      // @ts-expect-error Wrong type
      if (touched.additionalAppointments?.[index]?.startDateTime) {
        setFieldTouched(getCalendarInputName(index), false);
      }
      // if next appointment was the last one and moved down an index, set the touched to false
      const nextAppointmentIndex = index + 1;
      if (
        // @ts-expect-error Wrong type
        touched.additionalAppointments?.[nextAppointmentIndex]?.startDateTime &&
        !additionalAppointments[nextAppointmentIndex]
      ) {
        setFieldTouched(getCalendarInputName(nextAppointmentIndex), false);
      }
    }
  };

  const appointmentLengthOptions = getLengthOptions({
    user,
    appointmentType: values.appointmentType as AppointmentType,
    serviceId: values.serviceId ? Number(values.serviceId) : undefined,
    currentLength: values.appointmentLengthInMinutes
      ? Number(values.appointmentLengthInMinutes)
      : undefined,
  });

  const durationForm: FormData<DurationFormState> = {
    title: 'Add another appointment',
    initialValues: {
      appointmentLengthInMinutes: parseInt(
        values.appointmentLengthInMinutes || DEFAULT_APT_LENGTH,
        10,
      ),
    },
    inputRows: [
      [
        {
          inputType: 'select',
          label: 'Appointment length',
          tooltip: 'How long do you want to schedule the new appointment for?',
          name: 'appointmentLengthInMinutes',
          options: (appointmentLengthOptions || [])?.map(option => ({
            value: Number(option.value) as unknown as string,
            label: String(option.label),
          })),
        },
      ],
    ],
    onSubmit: ({ appointmentLengthInMinutes }) => {
      onAddSession(String(appointmentLengthInMinutes));
      setIsDurationModalOpen(false);
    },
    submitButtonText: 'Add',
    validationSchema: durationFormSchema,
  };

  const hideInput =
    values.appointmentType === 'WALK_IN' ||
    (!allowSwitchToBookingLink && values.isBookingLink);

  if (hideInput) return null;

  const isCalendarDisabled = values.isBookingLink;
  // Booking links and queued walk-ins are scheduled later, so an empty date is
  // valid there and must not be flagged.
  const isDateRequired =
    !values.isBookingLink && selectedAppointment?.status !== 'QUEUED';

  const handleBookingLinkClick = () => {
    setFieldValue('isBookingLink', !values.isBookingLink);
  };

  return (
    <>
      <div className="input-group no-gap">
        <Typography variant="h2" mb={2.5}>
          Date & Time
        </Typography>
        <div className="inputs">
          <CalendarInput
            error={errors?.startDateTime}
            appointmentLengthInMinutes={values.appointmentLengthInMinutes}
            startDateTime={values.startDateTime || ''}
            rescheduleAppointmentId={selectedAppointment?.appointmentId}
            onChange={onChangeMainSession}
            onDelete={
              values?.additionalAppointments?.length
                ? () => onDeleteSession(-1)
                : undefined
            }
            takenSlots={getTakenSessionSlots(-1, values)}
            disabled={isCalendarDisabled}
            showRequired={isDateRequired && !values.startDateTime}
          />
          {values.additionalAppointments?.map((session, index) => (
            <CalendarInput
              name={getCalendarInputName(index)}
              // @ts-expect-error Wrong type
              error={errors?.[getCalendarInputName(index)]}
              // eslint-disable-next-line react/no-array-index-key
              key={`${values.employeeId}-appointment-form-${index}`}
              appointmentLengthInMinutes={session.appointmentLengthInMinutes}
              startDateTime={session.startDateTime}
              onChange={value => onChangeSession(index, value)}
              onDelete={() => onDeleteSession(index)}
              takenSlots={getTakenSessionSlots(index, values)}
              disabled={isCalendarDisabled}
              showRequired={isDateRequired && !session.startDateTime}
            />
          ))}
          {((allowMultiSession && !isCalendarDisabled) ||
            allowSwitchToBookingLink) && (
            <Stack
              className="composer-date-actions"
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              sx={{ fontSize: theme.typography.body2.fontSize }}>
              {allowMultiSession && !isCalendarDisabled && (
                <Button
                  variant="light"
                  color="info"
                  size="small"
                  sx={ACTION_SX}
                  onClick={() => setIsDurationModalOpen(true)}>
                  {!allowSwitchToBookingLink && (
                    <AddIcon sx={{ fontSize: '1em', mr: 0.25 }} />
                  )}
                  Add another appointment
                </Button>
              )}
              {allowSwitchToBookingLink && (
                <Button
                  variant="light"
                  color="info"
                  sx={ACTION_SX}
                  onClick={handleBookingLinkClick}>
                  {values.isBookingLink
                    ? 'Select a date & time instead'
                    : 'Send a booking link instead'}
                </Button>
              )}
            </Stack>
          )}
        </div>
      </div>
      {isDurationModalOpen && (
        <FormSmallModal
          open
          onClose={() => setIsDurationModalOpen(false)}
          form={durationForm}
          allowUntouched
        />
      )}
    </>
  );
}

export default AppointmentDate;
